import styled, { css } from 'styled-components';
import { Typography, IconButton, CircularProgress } from '@mui/material';
import { Button } from 'shared-ui';

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 46px;
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

export const SmallButton = styled(Button)`
  && {
    padding-left: 14px;
    padding-right: 15px;
    min-width: unset;
  }
`;

export const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 24px;
  row-gap: 4px;
  align-items: start;
`;

export const DropzoneWrapper = styled.div`
  grid-column: 1 / -1;
  margin-top: 10px;
`;

export const RadioGroupWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RadioLabel = styled(Typography)`
  && {
    font-weight: 700;
    margin-bottom: 5px;
  }
`;

export const RadioRow = styled.div`
  display: flex;
  align-items: center;
  height: 44px;
`;

export const IntervalRow = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 24px;
  align-items: end;
`;

export const SchedulePickersWrapper = styled.div`
  grid-column: 2 / -1;
  display: grid;
  grid-template-columns: 20% 20% 20%;
  column-gap: 24px;
  align-items: end;
`;

export const IntervalInputWrapper = styled.div`
  position: relative;
  align-self: end;
`;

export const FormatWrapper = styled.div`
  display: grid;
  align-items: end;
  gap: 16px;
  grid-template-columns: 1fr 60px;
`;

export const FormatDetailsButton = styled(IconButton)(
  ({ disabled }) => css`
    width: 40px;
    height: 40px;
    margin-bottom: 4px;

    ${disabled &&
    css`
      opacity: 0.6;
    `}
  `,
);

export const DisabledOverlay = styled.div<{ $isDisabled: boolean }>(
  ({ $isDisabled }) => css`
    pointer-events: ${$isDisabled ? 'none' : 'auto'};
  `,
);

export const DropzoneTitleWrapper = styled.div`
  display: flex;
`;

export const Required = styled.span(
  ({ theme: { palette } }) => css`
    color: ${palette.error.main};
    margin-left: 5px;
  `,
);

export const KeyPresent = styled.span`
  color: rgba(0, 0, 0, 0.6);
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0;
  vertical-align: middle;
`;

export const LifecycleRow = styled.div<{ $hasError: boolean }>(
  ({ $hasError }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    background-color: ${$hasError ? '#FDECEA' : '#f5f5f5'};
    border-radius: 12px;
    padding: 12px;
    margin-bottom: 24px;
  `,
);

export const LifecycleLabelGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

export const LifecycleErrorText = styled(Typography)(
  ({ theme: { palette } }) => css`
    && {
      font-size: 12px;
      color: ${palette.error.main};
      line-height: 1;
    }
  `,
);

export const LifecycleLabel = styled(Typography)<{ $hasError?: boolean }>(
  ({ $hasError }) => css`
    && {
      font-family: Montserrat, sans-serif;
      font-weight: 600;
      font-size: 14px;
      line-height: 1;
      letter-spacing: 0;
      vertical-align: middle;
      color: ${$hasError ? '#ED715D' : 'rgba(0, 0, 0, 0.6)'};
    }
  `,
);

export const LifecycleButtonWrapper = styled.div<{ $hasError?: boolean; $isDisabled?: boolean }>(
  ({ $hasError, $isDisabled, theme: { palette } }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    background-color: ${$isDisabled ? palette.action.disabledBackground : $hasError ? '#F5B5AB' : '#f3f7fe'};
    border-radius: 100%;
  `,
);

export const LifecycleButton = styled(IconButton)<{ $hasError?: boolean }>(
  ({ theme: { palette }, $hasError }) => css`
    && {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: ${$hasError ? '#ED715D' : '#2d6ce6'};
      color: ${palette.common.white};
      width: 40px;
      height: 40px;
      z-index: 1;
      user-select: none;

      & svg {
        margin-left: 2px;
      }

      &:hover {
        background-color: ${$hasError ? '#ED715D' : '#2d6ce6'};
      }

      &:disabled {
        background-color: ${palette.action.disabledBackground};
        color: ${palette.action.disabled};
      }
    }
  `,
);

export const LifecycleLoader = styled(CircularProgress)<{ $hasError?: boolean }>(
  ({ theme: { palette }, $hasError }) => css`
    && {
      position: absolute;
      top: calc(50% - 28px);
      left: calc(50% - 28px);
      color: ${$hasError ? '#F5B5AB' : palette.primary.light};
      animation-duration: 2.8s;

      & circle {
        animation-duration: 2.8s;
      }
    }
  `,
);
